import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { getSupabaseSession } from "@/app/_module/services/auth";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";

// Allow overriding which agency's chat to view (for sub-agency switching)
// Uses sessionStorage so each tab can have its own override
export function setTeamChatAgencyOverride(agencyId: string | null) {
  if (typeof window === 'undefined') return;
  if (agencyId) {
    sessionStorage.setItem('team-chat-agency-override', agencyId);
  } else {
    sessionStorage.removeItem('team-chat-agency-override');
  }
}

function getAgencyOverride(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem('team-chat-agency-override');
}

async function authHeaders() {
  const session = await getSupabaseSession();
  const agencyId = getAgencyOverride() || AgencyTokenManager.getCurrentAgencyId();
  return {
    Authorization: `Bearer ${session?.access_token}`,
    "Content-Type": "application/json",
    ...(agencyId && { "x-agency-id": agencyId }),
  };
}

async function fetchJson(url: string, options?: RequestInit) {
  const headers = await authHeaders();
  const res = await fetch(getApiUrl(url), { ...options, headers: { ...headers, ...options?.headers } });
  if (!res.ok) {
    const errBody = await res.text().catch(() => "");
    console.error(`Team chat API error: ${res.status} ${url}`, errBody);
    throw new Error(`Team chat API error: ${res.status}`);
  }
  // Some endpoints return empty body (204)
  const text = await res.text();
  if (!text) return null;
  const json = JSON.parse(text);
  // Backend wraps in { statusCode, message, data: { success, data } }
  const outer = json.data;
  if (outer && typeof outer === 'object' && 'data' in outer) {
    return outer.data;
  }
  return outer;
}

export interface Channel {
  channel: {
    id: string;
    agencyId: string;
    name: string;
    slug: string;
    description?: string;
    type: "channel" | "dm";
    isDefault: boolean;
    isPrivate: boolean;
    isAnnouncement?: boolean;
    displayOrder?: number;
    lastMessageAt?: string;
    createdAt: string;
  };
  unreadCount: number;
  agencyName?: string; // Present for cross-agency channels in agency-wide mode
  lastMessage?: {
    id: string;
    content: string;
    senderName: string;
    createdAt: string;
    messageType: string;
  };
}

export interface Message {
  id: string;
  channelId: string;
  senderId: string;
  content?: string;
  messageType: "text" | "system" | "sales_win" | "insurasim_score";
  metadata?: Record<string, any>;
  isEdited: boolean;
  editedAt?: string;
  createdAt: string;
  sender: {
    id: string;
    name: string;
    email: string;
    profileImageUrl?: string;
  };
  attachments?: Array<{
    id: string;
    fileName: string;
    fileUrl: string;
    fileType?: string;
    fileSize?: number;
  }>;
  reactions?: Array<{
    id: string;
    emoji: string;
    userId: string;
    user?: { id: string; name: string };
  }>;
  replyCount?: number;
  parentMessageId?: string;
  parentMessage?: {
    id: string;
    content?: string;
    sender?: { id: string; name: string };
  };
}

export interface ChannelMember {
  id: string;
  channelId: string;
  userId: string;
  role: "owner" | "member";
  isMuted: boolean;
  agencyRole?: "owner" | "admin" | "agent" | "viewer";
  agencyTitle?: string | null;
  agencyName?: string | null;
  leaderboardRank?: number | null; // 1=gold, 2=silver, 3=bronze
  totalPremium?: number | null;
  user: {
    id: string;
    name: string;
    email: string;
    profileImageUrl?: string;
    isOnline?: boolean;
    userStatus?: string;
  };
}

// Life insurance custom emojis + common ones
export const CUSTOM_EMOJIS = [
  { emoji: "🏆", label: "Trophy", category: "sales" },
  { emoji: "💰", label: "Money Bag", category: "sales" },
  { emoji: "📋", label: "Clipboard", category: "insurance" },
  { emoji: "🛡️", label: "Shield", category: "insurance" },
  { emoji: "💎", label: "Gem", category: "sales" },
  { emoji: "🔥", label: "Fire", category: "hype" },
  { emoji: "🚀", label: "Rocket", category: "hype" },
  { emoji: "💪", label: "Flexed Biceps", category: "hype" },
  { emoji: "🎯", label: "Bullseye", category: "sales" },
  { emoji: "📞", label: "Phone", category: "insurance" },
  { emoji: "✍️", label: "Writing", category: "insurance" },
  { emoji: "🤝", label: "Handshake", category: "sales" },
  { emoji: "📝", label: "Policy Signed", category: "insurance" },
  { emoji: "💵", label: "Dollar", category: "sales" },
  { emoji: "🏠", label: "House", category: "insurance" },
  { emoji: "👨‍👩‍👧‍👦", label: "Family Protected", category: "insurance" },
  { emoji: "❤️", label: "Heart", category: "insurance" },
  { emoji: "⭐", label: "Star", category: "hype" },
  { emoji: "👏", label: "Clap", category: "hype" },
  { emoji: "🎉", label: "Party", category: "hype" },
  { emoji: "😂", label: "Laughing", category: "common" },
  { emoji: "👍", label: "Thumbs Up", category: "common" },
  { emoji: "👎", label: "Thumbs Down", category: "common" },
  { emoji: "😍", label: "Heart Eyes", category: "common" },
  { emoji: "🤔", label: "Thinking", category: "common" },
  { emoji: "😮", label: "Surprised", category: "common" },
  { emoji: "😢", label: "Sad", category: "common" },
  { emoji: "😤", label: "Huffing", category: "common" },
  { emoji: "🙏", label: "Pray", category: "common" },
  { emoji: "💀", label: "Skull", category: "common" },
  { emoji: "🫡", label: "Salute", category: "common" },
  { emoji: "🐐", label: "GOAT", category: "hype" },
  { emoji: "👑", label: "Crown", category: "hype" },
  { emoji: "💯", label: "100", category: "hype" },
  { emoji: "☠️", label: "Killer", category: "hype" },
  { emoji: "🦅", label: "Eagle", category: "hype" },
];

export interface Gif {
  id: string;
  url: string;
  preview: string;
  title?: string;
}

/** Which KLIPY library to browse. Same request and response shape for both. */
export type MediaKind = "gifs" | "stickers" | "memes" | "clips";

export interface GifPage {
  gifs: Gif[];
  hasNext: boolean;
  /** false when the proxy errored — lets the picker say so instead of "no results". */
  ok: boolean;
}

export const teamChatService = {
  getChannels: (): Promise<Channel[]> =>
    fetchJson(endpoint.teamChat.channels),

  getMessages: (channelId: string, cursor?: string, limit = 50): Promise<{ messages: Message[]; hasMore: boolean; nextCursor?: string }> =>
    fetchJson(`${endpoint.teamChat.channelMessages(channelId)}?limit=${limit}${cursor ? `&cursor=${cursor}` : ""}`),

  sendMessage: (channelId: string, content: string): Promise<Message> =>
    fetchJson(endpoint.teamChat.sendMessage(channelId), {
      method: "POST",
      body: JSON.stringify({ content }),
    }),

  editMessage: (messageId: string, content: string): Promise<Message> =>
    fetchJson(endpoint.teamChat.editMessage(messageId), {
      method: "PATCH",
      body: JSON.stringify({ content }),
    }),

  deleteMessage: (messageId: string): Promise<void> =>
    fetchJson(endpoint.teamChat.deleteMessage(messageId), { method: "DELETE" }),

  markRead: (channelId: string): Promise<void> =>
    fetchJson(endpoint.teamChat.markRead(channelId), { method: "POST" }),

  getUnreadCounts: (): Promise<Record<string, number>> =>
    fetchJson(endpoint.teamChat.unreadCounts),

  getMembers: (channelId: string): Promise<ChannelMember[]> =>
    fetchJson(endpoint.teamChat.channelMembers(channelId)),

  toggleAnnouncement: (channelId: string): Promise<{ isAnnouncement: boolean }> =>
    fetchJson(`v1/team-chat/channels/${channelId}/announcement`, { method: "PATCH" }),

  reorderChannel: (channelId: string, direction: 'up' | 'down'): Promise<void> =>
    fetchJson(`v1/team-chat/channels/${channelId}/reorder`, {
      method: "PATCH",
      body: JSON.stringify({ direction }),
    }),

  renameChannel: (channelId: string, name: string): Promise<any> =>
    fetchJson(`v1/team-chat/channels/${channelId}/rename`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),

  deleteChannel: (channelId: string): Promise<void> =>
    fetchJson(`v1/team-chat/channels/${channelId}`, { method: "DELETE" }),

  createChannel: (name: string, description?: string, isPrivate = false): Promise<any> =>
    fetchJson(endpoint.teamChat.channels, {
      method: "POST",
      body: JSON.stringify({ name, description, isPrivate }),
    }),

  getOrCreateDm: (userIds: string[], groupName?: string): Promise<any> =>
    fetchJson(endpoint.teamChat.dm, {
      method: "POST",
      body: JSON.stringify({ userIds, ...(groupName ? { groupName } : {}) }),
    }),

  addReaction: (messageId: string, emoji: string): Promise<any> =>
    fetchJson(endpoint.teamChat.addReaction(messageId), {
      method: "POST",
      body: JSON.stringify({ emoji }),
    }),

  removeReaction: (messageId: string, emoji: string): Promise<void> =>
    fetchJson(endpoint.teamChat.removeReaction(messageId, encodeURIComponent(emoji)), { method: "DELETE" }),

  toggleMute: (channelId: string): Promise<{ isMuted: boolean }> =>
    fetchJson(endpoint.teamChat.muteChannel(channelId), { method: "PATCH" }),

  uploadFile: async (channelId: string, file: File, content?: string): Promise<Message> => {
    const session = (await import("@/app/_module/services/auth")).getSupabaseSession();
    const token = (await session)?.access_token;
    const formData = new FormData();
    formData.append("file", file);
    if (content) formData.append("content", content);

    const res = await fetch(
      (await import("@/app/_module/services/config")).getApiUrl(`v1/team-chat/channels/${channelId}/upload`),
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          ...((getAgencyOverride() || AgencyTokenManager.getCurrentAgencyId())
            ? { "x-agency-id": (getAgencyOverride() || AgencyTokenManager.getCurrentAgencyId())! }
            : {}),
        },
        body: formData,
      }
    );
    if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
    const json = await res.json();
    return json.data;
  },

  // GIF + sticker search via backend proxy — the backend normalizes KLIPY's
  // file[size][format] tree down to { id, url, preview }.
  searchMedia: async (kind: MediaKind, query: string, limit = 24, page = 1): Promise<GifPage> => {
    try {
      const data = await fetchJson(
        `v1/team-chat/${kind}/search?q=${encodeURIComponent(query)}&limit=${limit}&page=${page}`,
      );
      return { gifs: data?.results || [], hasNext: data?.hasNext === true, ok: true };
    } catch { return { gifs: [], hasNext: false, ok: false }; }
  },

  /**
   * AI sticker: start a job, then poll until it resolves. ~10s, so the caller
   * shows progress. Resolves to a File ready for the normal upload path —
   * KLIPY hands back base64, not a URL.
   */
  generateSticker: async (prompt: string, onTick?: () => void): Promise<File> => {
    const started = await fetchJson("v1/team-chat/stickers/generate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt }),
    });
    const id = started?.id;
    if (!id) throw new Error("Couldn't start generation.");

    // ~10s typical. Cap the wait so a stuck job fails loudly instead of
    // spinning forever.
    for (let i = 0; i < 40; i++) {
      await new Promise(r => setTimeout(r, 1500));
      onTick?.();
      const poll = await fetchJson(`v1/team-chat/stickers/generated/${id}`);
      if (poll?.status === "success" && poll.imageBase64) {
        const bytes = Uint8Array.from(atob(poll.imageBase64), c => c.charCodeAt(0));
        return new File([bytes], `sticker-${id}.png`, { type: "image/png" });
      }
      if (poll?.status === "failed") throw new Error("Generation failed. Try a different description.");
    }
    throw new Error("Generation timed out. Try again.");
  },

  /** Search-as-you-type terms. Never throws — a dead list must not block typing. */
  mediaSuggestions: async (query: string): Promise<string[]> => {
    try {
      const data = await fetchJson(`v1/team-chat/media-suggestions?q=${encodeURIComponent(query)}`);
      return data?.suggestions || [];
    } catch { return []; }
  },

  trendingMedia: async (kind: MediaKind, limit = 24, page = 1): Promise<GifPage> => {
    try {
      const data = await fetchJson(`v1/team-chat/${kind}/trending?limit=${limit}&page=${page}`);
      return { gifs: data?.results || [], hasNext: data?.hasNext === true, ok: true };
    } catch { return { gifs: [], hasNext: false, ok: false }; }
  },

  // Message search
  searchMessages: (channelId: string, query: string, limit = 20): Promise<Message[]> =>
    fetchJson(`v1/team-chat/channels/${channelId}/search?q=${encodeURIComponent(query)}&limit=${limit}`),

  // Pin management
  pinMessage: (messageId: string): Promise<any> =>
    fetchJson(`v1/team-chat/messages/${messageId}/pin`, { method: "POST" }),

  unpinMessage: (messageId: string): Promise<void> =>
    fetchJson(`v1/team-chat/messages/${messageId}/pin`, { method: "DELETE" }),

  getPinnedMessages: (channelId: string): Promise<Message[]> =>
    fetchJson(`v1/team-chat/channels/${channelId}/pinned`),

  // Threading / replies
  sendReply: (channelId: string, content: string, parentMessageId: string): Promise<Message> =>
    fetchJson(endpoint.teamChat.sendMessage(channelId), {
      method: "POST",
      body: JSON.stringify({ content, parentMessageId }),
    }),

  getThread: (messageId: string, cursor?: string, limit = 50): Promise<{ messages: Message[]; hasMore: boolean; nextCursor?: string }> =>
    fetchJson(`v1/team-chat/messages/${messageId}/thread?limit=${limit}${cursor ? `&cursor=${cursor}` : ""}`),

  // Read receipts
  getReadReceipts: (channelId: string): Promise<Record<string, string>> =>
    fetchJson(`v1/team-chat/channels/${channelId}/read-receipts`),
};
